import {
    AdminPreferences,
    SearchInterface,
    KendraConst,
    IMetrics,
    GeneratedAnswerInterface
} from "@amzn/ask-legal-domain";
import { Alert, Pagination, TextContent } from "@amzn/awsui-components-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Builder } from "builder-pattern";
import { AppContext } from "../../setup/context";
import { useAPI } from "../../hooks/api-hook";
import { SearchResultPanel } from "../../components/search/ResultPanel";
import { SmartSearchFilterView } from "../../components/search/search-components/SmartSearchFilterView";
import { LoadingSpinner } from "../../components/common/loading-spinner";
import { ErrorFlashbar } from "../../components/common/ErrorFlashbar";
import { SearchStreamingContainer } from "../../components/search/search-components/SearchStreamingContainer";
import { UIModel } from "../../model/ui-model";
import "../../styles/component/layout/SearchResultPageLayout.scss";
import "../../styles/component/layout/PageBaseLayout.scss";
import "../../styles/component/search/automated-answer.scss";

interface GlobalSearchHomeProps {
    queryText?: string;
}

const INITIAL_PAGE_SIZE = 10;
const INITIAL_PAGE_NUMBER = 1;

export const GlobalSearchHome: React.FC<GlobalSearchHomeProps> = ({ queryText }) => {
    const context = React.useContext(AppContext);
    const searchRunner = useAPI(
        context.getSearchAPI().search
    );
    const recordViewHistoryRunner = useAPI(
        context.getCommonAPI().recordViewHistory
    );
    const navigate = useNavigate();
    const queryInputState = UIModel.State.use<SearchInterface.SearchQueryInput>({});

    const [authors, setAuthors] = React.useState<string[]>([]);
    const [pageLibs, setPageLibs] = React.useState<string[]>([]);
    const [availableInstances, setAvailableInstances] = React.useState<string[]>([]);
    const [hasLegalOnlyAccess, setHasLegalOnlyAccess] = React.useState(false);

    // Use default search preferences for global search
    const searchPreferences = AdminPreferences.DEFAULT_ADMIN_PREFERENCES.search!;

    const initAuthorOptions = React.useCallback(() => {
        const allAuthors: string[] = [];
        searchRunner.data.output.result.ResultItems.forEach(r => {
            const author = r.DocumentAttributes.find(a =>
                a.Key === KendraConst.ContainerIngestionKendraAttributes.Default.Authors
            );
            if (author?.Value?.StringListValue) {
                allAuthors.push(...author.Value.StringListValue);
            }
        });
        setAuthors([...new Set(allAuthors)]);
    }, [searchRunner.data.output]);

    const initPageLibraries = React.useCallback(() => {
        const pageLibs: string[] = [];
        searchRunner.data.output.result.ResultItems.forEach(r => {
            const pageLib = r.DocumentAttributes.find(a =>
                a.Key === KendraConst.ContainerIngestionKendraAttributes.Custom.PageLibraryId
            );
            if (pageLib?.Value?.StringValue) {
                pageLibs.push(pageLib.Value.StringValue);
            }
        });
        setPageLibs([...new Set(pageLibs)]);
    }, [searchRunner.data.output]);

    const initAvailableInstances = React.useCallback(() => {
        if (!searchRunner.data.output?.result?.FacetResults) return;
        const instanceFacet = searchRunner.data.output.result.FacetResults.find(facet =>
            facet.DocumentAttributeKey === KendraConst.ContainerIngestionKendraAttributes.Default.InstanceId
        );
        const instancesFromSearch = instanceFacet?.DocumentAttributeValueCountPairs
            ?.filter(pair => pair.DocumentAttributeValue?.StringValue)
            .map(pair => pair.DocumentAttributeValue.StringValue as string) || [];
        setAvailableInstances(instancesFromSearch);
    }, [searchRunner.data.output]);

    const getClientSidePaginatedSearchResults = React.useCallback(() => {
        const { pageNumber, pageSize } = queryInputState.value;
        const startIndex = (pageNumber - 1) * pageSize;
        const endIndex = pageNumber * pageSize;
        const paginatedResults = searchRunner.data.output.result.ResultItems.slice(startIndex, endIndex);

        return SearchInterface.SearchQueryResponse.create({
            result: {
                ...searchRunner.data.output.result,
                ResultItems: paginatedResults
            },
            isLegalOnlyAuthorized: searchRunner.data.output.isLegalOnlyAuthorized
        });
    }, [queryInputState.value, searchRunner.data.output]);

    const constructGlobalSearchQueryInput = React.useCallback((
        queryText: string
    ): SearchInterface.SearchQueryInput => {
        // Create a global search query without instanceId
        return Builder<SearchInterface.SearchQueryInput>()
            .queryText(queryText)
            .pageSize(INITIAL_PAGE_SIZE)
            .pageNumber(INITIAL_PAGE_NUMBER)
            .build();
    }, []);

    const [isQueryInitialized, setIsQueryInitialized] = React.useState(false);
    const lastSearchQueryRef = React.useRef<string>("");

    const streamingRequest = React.useMemo<GeneratedAnswerInterface.GeneratedAnswerRequest | null>(() => {
        if (!queryInputState.value) return null;
        return {
            query: queryInputState.value.queryText,
            showStrictlyLegalOnly: queryInputState.value.showStrictlyLegalOnly ?? false,
            showSharedContent: queryInputState.value.showSharedContent ?? false,
            filterConfiguration: queryInputState.value.filterConfiguration
        };
    }, [
        queryInputState.value?.queryText,
        queryInputState.value?.showStrictlyLegalOnly,
        queryInputState.value?.showSharedContent,
        queryInputState.value?.filterConfiguration
    ]);

    const createQuerySignature = React.useCallback((query: SearchInterface.SearchQueryInput): string => {
        return JSON.stringify({
            text: query.queryText,
            filters: query.filterConfiguration || [],
            page: query.pageNumber,
            sort: query.sortingConfiguration || []
        });
    }, []);

    const executeSearchImmediate = React.useCallback((query: SearchInterface.SearchQueryInput) => {
        const querySignature = createQuerySignature(query);
        if (querySignature === lastSearchQueryRef.current) {
            return;
        }
        lastSearchQueryRef.current = querySignature;
        searchRunner.submitRun(query);
        recordViewHistoryRunner.submitRun(
            IMetrics.RecordViewHistoryInput.create({
                url: `/search?queryText=${queryText}`,
                entityId: "global",
                type: "Search" // Use existing Type - TODO change this later
            })
        );
    }, [createQuerySignature, searchRunner, recordViewHistoryRunner, queryText]);

    React.useEffect(() => {
        queryInputState.setValue(constructGlobalSearchQueryInput(queryText));
        setAuthors([]);
        setPageLibs([]);
        setIsQueryInitialized(true);
    }, [queryText, constructGlobalSearchQueryInput]);

    React.useEffect(() => {
        if (!isQueryInitialized || !queryInputState.value?.queryText?.length) {
            return;
        }

        executeSearchImmediate(queryInputState.value);
    }, [queryInputState.value, isQueryInitialized, executeSearchImmediate]);

    React.useEffect(() => {
        if (searchRunner.status !== "Succeeded" || !searchRunner.data.output) return;

        if (authors.length === 0) {
            initAuthorOptions();
        }
        if (pageLibs.length === 0) {
            initPageLibraries();
        }
        if (availableInstances.length === 0) {
            initAvailableInstances();
        }
        setHasLegalOnlyAccess(searchRunner.data.output.isLegalOnlyAuthorized);
    }, [
        searchRunner.status,
        searchRunner.data.output,
        authors.length,
        pageLibs.length,
        availableInstances.length,
        initAuthorOptions,
        initPageLibraries,
        initAvailableInstances
    ]);

    // Validation check after all hooks have been called
    if (!queryText?.trim()) {
        return (
            <div className="top-margin-5p">
                <Alert
                    type="error"
                    header="Cannot search for an empty query"
                >
                    <TextContent>
                        Please provide a valid search query and try again.
                    </TextContent>
                </Alert>
            </div>
        );
    }

    return (
        <React.Fragment>
            {queryInputState.value &&
                <div className="search-page-layout">
                    <div className="search-page-layout-filter">
                        <SmartSearchFilterView
                            preferences={searchPreferences}
                            queryInputState={queryInputState}
                            authors={authors}
                            pageLibraries={pageLibs}
                            availableInstances={availableInstances}
                            hasLegalOnlyAccess={hasLegalOnlyAccess}
                            isSearchRunning={searchRunner.status === "Running"}
                            isGlobalSearch={true}
                        />
                    </div>
                    <div className="search-page-layout-result">
                        {/* AI Search Summary is enabled by default for global search */}
                        {streamingRequest && (
                            <SearchStreamingContainer
                                request={streamingRequest}
                                shouldStart={!!queryInputState.value?.queryText}
                            />
                        )}
                        {searchRunner.status === "Running" &&
                            <div className="vertical-center top-margin-5p">
                                <LoadingSpinner msg={`Searching for ${queryInputState.value.queryText} ...`}/>
                            </div>
                        }
                        {searchRunner.status === "Error" && (
                            <ErrorFlashbar error={searchRunner.data.err}/>
                        )}
                        {searchRunner.status === "Succeeded" && !!searchRunner.data.output && (
                            <React.Fragment>
                                <h4>
                                    Global Search Results for <em>"{queryInputState.value.queryText}"</em>
                                </h4>
                                {!!searchRunner.data.output.result.SpellCorrectedQueries &&
                                    <div className="search-result-spell-correction">
                                        {searchRunner.data.output.result.SpellCorrectedQueries.map(spellCorrectionQuery => (
                                            <div key={spellCorrectionQuery.SuggestedQueryText}>
                                                Do you mean "<a onClick={() => {
                                                    queryInputState.setValue(
                                                        constructGlobalSearchQueryInput(spellCorrectionQuery.SuggestedQueryText)
                                                    );
                                                    // Update URL without triggering page reload
                                                    navigate(`/search?queryText=${spellCorrectionQuery.SuggestedQueryText}`, { replace: true });
                                                }}>{spellCorrectionQuery.SuggestedQueryText}</a>"?
                                            </div>
                                        ))}
                                        <br />
                                    </div>
                                }
                                <SearchResultPanel
                                    results={getClientSidePaginatedSearchResults()}
                                    queryInputState={queryInputState}
                                />
                                <nav className="centered-pagination" aria-label="pagination">
                                    <Pagination
                                        ariaLabels={{
                                            nextPageLabel: "Next page",
                                            previousPageLabel: "Previous page",
                                            pageLabel: pageNumber =>
                                                `Page ${pageNumber} of all pages`
                                        }}
                                        currentPageIndex={queryInputState.value.pageNumber}
                                        onChange={({ detail }) =>
                                            queryInputState.setValue(
                                                Builder<SearchInterface.SearchQueryInput>(queryInputState.value)
                                                    .pageNumber(detail.currentPageIndex)
                                                    .build()
                                            )
                                        }
                                        pagesCount={Math.ceil(
                                            searchRunner.data.output.result.TotalNumberOfResults / queryInputState.value.pageSize
                                        )}
                                    />
                                </nav>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            }
        </React.Fragment>
    );
};
